import { usePaginatedPublicGroupChats } from "@/middleware/hooks/usePaginatedPublicGroupChats";
import PublicChatListItem from "./PublicChatListItem";
import { useEffect, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useToast } from "@/hooks/useToast";
import { usePublicGroupsUserRequests } from "@/middleware/hooks/usePublicGroupsUserRequests";

type Props = {
  searchTerm: string;
};

const PublicChatSkeleton = () => (
  <div className="flex items-center justify-start gap-2 truncate p-2 align-middle">
    <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
    <div className="h-9 w-full animate-pulse rounded-sm bg-muted" />
    <div className="h-9 w-14 shrink-0 animate-pulse rounded-md bg-muted" />
  </div>
);

export default function PublicChatList({ searchTerm }: Props) {
  const { toast } = useToast();

  const { data: activePublicGroupsUserRequests } =
    usePublicGroupsUserRequests();

  const {
    data,
    status,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  } = usePaginatedPublicGroupChats({
    searchTerm,
  });

  const results = useMemo(() => (data ? data.pages.flat() : []), [data]);

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? results.length + 1 : results.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 5,
  });

  useEffect(() => {
    if (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : "Cannot load groups",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  // Infinite scroll trigger
  useEffect(() => {
    const virtualItems = rowVirtualizer.getVirtualItems();
    const [lastItem] = [...virtualItems].reverse();
    if (
      lastItem &&
      lastItem.index >= results.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    rowVirtualizer,
    results.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const showInitialSkeleton = isFetching && status === "pending";
  // const showEmptyState = !isFetching && results.length === 0;

  return (
    <>
      {showInitialSkeleton ? (
        <>
          <PublicChatSkeleton />
          <PublicChatSkeleton />
          <PublicChatSkeleton />
        </>
      ) : (
        <div
          ref={parentRef}
          className="custom-scroll"
          style={{
            height: `100%`,
            width: `100%`,
            overflow: "auto",
          }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const isLoaderRow = virtualRow.index > results.length - 1;
              const pubChat = results[virtualRow.index];
              const isRequestPending = !!activePublicGroupsUserRequests?.some(
                (request) => request === pubChat.id,
              );

              return (
                <div
                  key={virtualRow.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {isLoaderRow ? (
                    hasNextPage && (
                      <>
                        <PublicChatSkeleton />
                        <PublicChatSkeleton />
                        <PublicChatSkeleton />
                      </>
                    )
                  ) : (
                    <PublicChatListItem
                      key={pubChat.id}
                      chat={pubChat}
                      isRequestPending={isRequestPending}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
